import React from "react";
import { Button, Table } from "antd";
import { observer } from "mobx-react-lite";
import { useModalStore } from "@/hooks/Modal/hooks";
import BatchAddProductModal from "../../../../../components/BatchAddProductModal";
import { useGlobalStore } from "@/store/hooks";
import OrderItemFormStore from "./OrderItemFormStore";
import HistoryAddedManualOrderItemList from "./components/HistoryAddedManualOrderItemList";
import { isEmpty, uuid } from "@/utils";
import Big from "big.js";
import { ManualOrderItem } from "./type";
import type { TableColumnsType } from "antd";
import "./index.less";
import { EditOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import AddOrUpdateOrderItemModal from "../AddOrUpdateOrderItemModal";
import InputNumber from "@/components/InputNumber";
import SectionTitle from "../SectionTitle";

interface OrderItemFormProps {
    store: OrderItemFormStore;
    hideTitle?: boolean;
    hideAdd?: boolean;
    hideHistoryAddedManualOrderItemList?: boolean;
    hideDelete?: boolean;
    hideCheckbox?: boolean;
    shopId?: number;
}

const OrderItemForm: React.FC<OrderItemFormProps> = (props) => {
    const {
        store,
        hideTitle = false,
        hideAdd = false,
        hideHistoryAddedManualOrderItemList = false,
        hideCheckbox = true,
        hideDelete = false,
        shopId,
    } = props;
    const modalStore = useModalStore();
    const { companyRoleStore } = useGlobalStore();
    const showSearchProductBtn = !companyRoleStore.isPureSupplier;
    function openUpdateModal(manualProduct: ManualOrderItem, autoFocus: string) {
        modalStore.openModal(AddOrUpdateOrderItemModal, {
            onUpdate: store.updateOrderItem,
            manualProduct,
            autoFocus,
        });
    }

    function updateQuantityOrderItem(manualProduct: ManualOrderItem, value: string | number) {
        if (value === "" || isEmpty(value)) return;

        const quantity = Number(value);

        if (Number.isNaN(quantity) || quantity < 1) return;

        store.updateOrderItem({ ...manualProduct, quantity });
    }

    function stepQuantityOrderItem(manualProduct: ManualOrderItem, step: 1 | -1) {
        const currentQuantity = Number(manualProduct.quantity);
        const quantity = Number.isNaN(currentQuantity) ? 1 : Math.max(1, currentQuantity + step);

        store.updateOrderItem({ ...manualProduct, quantity });
    }

    const columns: TableColumnsType<ManualOrderItem> = [
        {
            title: "商品名称",
            dataIndex: "title",
            width: "200px",
            render(_, manualProduct) {
                return (
                    <span style={{ wordBreak: "break-word" }}>
                        <span>{manualProduct.title}</span>
                        <EditOutlined
                            className="ml10"
                            title="修改"
                            onClick={() => openUpdateModal(manualProduct, "title")}
                        />
                    </span>
                );
            },
        },
        {
            title: "商品简称",
            dataIndex: "simpleTitle",
            width: "100px",
            render(_, manualProduct) {
                return (
                    <span style={{ wordBreak: "break-word" }}>
                        <span>{manualProduct.simpleTitle}</span>
                        <EditOutlined
                            className="ml10"
                            title="修改"
                            onClick={() => openUpdateModal(manualProduct, "simpleTitle")}
                        />
                    </span>
                );
            },
        },
        {
            title: "商品编码",
            dataIndex: "productExternalId",
            width: "150px",
            render(_, manualProduct) {
                return (
                    <span style={{ wordBreak: "break-word" }}>
                        <span>{manualProduct.productExternalId}</span>
                        <EditOutlined
                            className="ml10"
                            title="修改"
                            onClick={() => openUpdateModal(manualProduct, "productExternalId")}
                        />
                    </span>
                );
            },
        },
        {
            title: "数量",
            dataIndex: "quantity",
            width: "130px",
            render(_, manualProduct) {
                const quantity = Number(manualProduct.quantity);
                const disabledSubtract = Number.isNaN(quantity) || quantity <= 1;

                return (
                    <span className="add-order-item-table-quantity-cell">
                        <span className="add-order-item-quantity-stepper">
                            <button
                                type="button"
                                className="add-order-item-quantity-stepper-btn"
                                disabled={disabledSubtract}
                                onClick={() => stepQuantityOrderItem(manualProduct, -1)}
                            >
                                <MinusOutlined />
                            </button>
                            <InputNumber
                                size="small"
                                min={1}
                                precision={0}
                                value={manualProduct.quantity}
                                style={{ width: 56 }}
                                onChange={(value) => updateQuantityOrderItem(manualProduct, value)}
                            />
                            <button
                                type="button"
                                className="add-order-item-quantity-stepper-btn"
                                onClick={() => stepQuantityOrderItem(manualProduct, 1)}
                            >
                                <PlusOutlined />
                            </button>
                        </span>
                        {/* <EditOutlined
                            className="ml10"
                            title="修改"
                            onClick={() => openUpdateModal(manualProduct, "quantity")}
                        /> */}
                    </span>
                );
            },
        },
        {
            title: "单价",
            dataIndex: "price",
            width: "100px",
            render(_, manualProduct) {
                return (
                    <span style={{ wordBreak: "break-word" }}>
                        <span>{manualProduct.price}</span>
                        <EditOutlined
                            className="ml10"
                            title="修改"
                            onClick={() => openUpdateModal(manualProduct, "price")}
                        />
                    </span>
                );
            },
        },
        {
            title: "销售属性",
            dataIndex: "skuProperties",
            width: "180px",
            render(_, manualProduct) {
                return (
                    <span style={{ wordBreak: "break-word" }}>
                        <span>{manualProduct.skuProperties}</span>
                        <EditOutlined
                            className="ml10"
                            title="修改"
                            onClick={() => openUpdateModal(manualProduct, "skuProperties")}
                        />
                    </span>
                );
            },
        },
        {
            title: "SKU 编码",
            dataIndex: "skuExternalId",
            width: "150px",
            render(_, manualProduct) {
                return (
                    <span style={{ wordBreak: "break-word" }}>
                        <span>{manualProduct.skuExternalId}</span>
                        <EditOutlined
                            className="ml10"
                            title="修改"
                            onClick={() => openUpdateModal(manualProduct, "skuExternalId")}
                        />
                    </span>
                );
            },
        },
        {
            title: "重量(kg)",
            dataIndex: "weight",
            width: "100px",
            render(_, manualProduct) {
                return (
                    <span style={{ wordBreak: "break-word" }}>
                        <span>{manualProduct.weight}</span>
                        <EditOutlined
                            className="ml10"
                            title="修改"
                            onClick={() => openUpdateModal(manualProduct, "weight")}
                        />
                    </span>
                );
            },
        },
        // 如果不隐藏删除列，就加上操作列
        !hideDelete
            ? {
                  title: "操作",
                  key: "operating",
                  width: "80px",
                  render: (_, manualOrderItem, index: number) => (
                      <Button
                          key={manualOrderItem.key}
                          type="link"
                          size="small"
                          onClick={() => store.deleteOrderItem(index)}
                      >
                          删除
                      </Button>
                  ),
              }
            : null,
    ].filter(Boolean);

    return (
        <>
            {!hideTitle ? <SectionTitle title="商品信息" /> : null}
            <div className="mb10">
                {!hideAdd ? (
                    <Button
                        className="ant-btn-success mr10"
                        onClick={() => {
                            modalStore.openModal(AddOrUpdateOrderItemModal, {
                                onChange: store.addOrderItems,
                            });
                        }}
                    >
                        手工添加商品
                    </Button>
                ) : null}
                {showSearchProductBtn && !hideAdd && (
                    <Button
                        type="primary"
                        onClick={() => {
                            modalStore.openModal(BatchAddProductModal, {
                                defaultShopId: shopId,
                                allowZero: false,
                                onFinish(values) {
                                    const getWeight = (item) =>
                                        !isEmpty(item.weight) ? Big(item.weight).div(1000).toNumber() : undefined;
                                    const orderItems: ManualOrderItem[] = values.map(({ product, sku }) => {
                                        const source = sku || product;
                                        return {
                                            title: product.title,
                                            simpleTitle: sku?.simpleTitle || product.simpleTitle,
                                            quantity: 1,
                                            price: sku ? sku.price : 0,
                                            skuProperties: sku?.propertiesName ?? "",
                                            skuExternalId: sku?.externalId,
                                            picture: product.picture,
                                            key: uuid(),
                                            weight: getWeight(source),
                                            productOriginalId: product.originalId,
                                            skuOriginalId: sku?.originalId,
                                            productExternalId: product.externalId,
                                        };
                                    });
                                    store.addOrderItems(orderItems);
                                },
                            });
                        }}
                    >
                        搜索添加商品
                    </Button>
                )}
            </div>
            <Table
                className="add-order-item-table"
                size="medium"
                scroll={{ x: "100%" }}
                dataSource={store.orderItems}
                columns={columns}
                pagination={false}
                rowKey={(data) => data.key}
                rowSelection={
                    !hideCheckbox
                        ? {
                              type: "checkbox",
                              selectedRowKeys: store.selectedRowKeys,
                              onChange: store.setSelectedRowKeys,
                          }
                        : undefined
                }
            />
            {!hideHistoryAddedManualOrderItemList ? <HistoryAddedManualOrderItemList store={store} /> : null}
        </>
    );
};

export default observer(OrderItemForm);
